/**
 * usePackPage / usePack / usePackPrice — React Query hooks for the Pack PDP.
 *
 * `usePackPage` is the primary entry point: it hits the new BFF page bundle
 * `GET /api/v1/pages/pack/:slug` which fans out to products + tss-svc-pricing via storefront-bff
 * server-side and returns the hydrated pack plus resolved pack pricing in
 * one round-trip. It also seeds:
 *   - ['pack', slug]                                      → usePack(slug)
 *   - ['pack-price', packId, currency, country]            → usePackPrice
 *
 * `usePack` and `usePackPrice` are kept for backwards compatibility and
 * read from the seeded cache; they no longer perform their own fetches.
 */

'use client';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Pack, PackPricing } from '@template-project/storefront-bff-client';
import { createBffClient } from '@/lib/bff/create-storefront-client';
import {
  PackEntity,
  PackItemEntity,
  PackProductSnapshot,
  PackVariantSnapshot,
  ResolvedPackPriceEntity,
} from '@/domain/entities/pack.entity';
import type { CurrencyAmount } from '@/domain/value-objects/currency-amount.vo';

function mapVariant(v: NonNullable<NonNullable<Pack['items'][number]['variant']>>): PackVariantSnapshot {
  return {
    id: v.id,
    product_id: v.product_id,
    title: v.title,
    price: v.price as CurrencyAmount,
    compare_at_price: v.compare_at_price as CurrencyAmount | undefined,
    sku: v.sku,
    enabled: v.enabled,
    requires_shipping: v.requires_shipping,
    image_id: v.image_id,
    variation_attributes: v.variation_attributes,
  };
}

function mapProduct(p: NonNullable<Pack['items'][number]['product']>): PackProductSnapshot {
  return {
    id: p.id,
    title: p.title,
    status: p.status,
    images: p.images,
    options: p.options,
    variants: p.variants.map(mapVariant),
  };
}

function mapItem(it: Pack['items'][number]): PackItemEntity {
  return {
    id: it.id,
    pack_id: it.pack_id,
    product_id: it.product_id,
    variant_id: it.variant_id,
    quantity: it.quantity,
    is_required: it.is_required,
    position: it.position,
    product: it.product ? mapProduct(it.product) : undefined,
    variant: it.variant ? mapVariant(it.variant) : undefined,
  };
}

function mapPack(p: Pack): PackEntity {
  return {
    id: p.id,
    name: p.name,
    slug: p.slug,
    status: p.status,
    description: p.description,
    description_html: p.description_html,
    image_url: p.image_url,
    type: p.type,
    min_items: p.min_items,
    max_items: p.max_items,
    tags: p.tags,
    items: p.items.map(mapItem),
  };
}

function mapPackPrice(p: PackPricing | null): ResolvedPackPriceEntity | null {
  if (!p) return null;
  const compareAtDiscount = p.discounts.find((d) => d.source === 'pack' || d.source === 'compare_at');
  return {
    pack_id: p.pack_id,
    currency_code: p.currency_code,
    country_code: p.country_code,
    price: p.price as CurrencyAmount,
    compare_at_price: p.compare_at_price as CurrencyAmount | undefined,
    discount_type: compareAtDiscount?.type === 'percent_off'
      ? 'percentage_off'
      : compareAtDiscount?.type === 'fixed_price'
        ? 'fixed_price'
        : compareAtDiscount?.type === 'amount_off'
          ? 'fixed_discount'
          : undefined,
    discount_value: compareAtDiscount?.percent ?? compareAtDiscount?.amount?.display,
    price_list_name: p.price_list_name,
  };
}

export interface UsePackPageInput {
  slug?: string;
  currency?: string;
  country?: string;
}

export interface PackPageData {
  pack: PackEntity;
  pricing: ResolvedPackPriceEntity | null;
}

export function usePackPage(input: UsePackPageInput) {
  const { slug, currency = 'USD', country } = input;
  const queryClient = useQueryClient();

  return useQuery<PackPageData | null>({
    queryKey: ['pack-page', slug, currency, country],
    queryFn: async () => {
      if (!slug) return null;
      const client = createBffClient();
      const res = await client.getPackPage(slug, { currency, country });
      const pack = mapPack(res.data.pack);
      const pricing = mapPackPrice(res.data.pricing);

      queryClient.setQueryData(['pack', slug], pack);
      if (pricing) {
        queryClient.setQueryData(['pack-price', pack.id, currency, country], pricing);
      }

      return { pack, pricing };
    },
    enabled: !!slug,
    staleTime: 60 * 1000,
    retry: 1,
  });
}

// ---------------------------------------------------------------------------
// Backwards-compatible read-from-cache hooks
// ---------------------------------------------------------------------------

export interface UsePackInput {
  slug?: string;
  packId?: string;
}

export function usePack(input: UsePackInput) {
  const { slug, packId } = input;
  const queryClient = useQueryClient();

  return useQuery<PackEntity | null>({
    queryKey: ['pack', slug ?? packId],
    queryFn: async () => {
      const cached = queryClient.getQueryData<PackEntity | null>(['pack', slug ?? packId]);
      return cached ?? null;
    },
    enabled: !!(slug || packId),
    staleTime: 60 * 1000,
    retry: 1,
  });
}

export interface UsePackPriceInput {
  packId?: string;
  currencyCode?: string;
  countryCode?: string;
}

export function usePackPrice(input: UsePackPriceInput) {
  const { packId, currencyCode, countryCode } = input;
  const queryClient = useQueryClient();

  return useQuery<ResolvedPackPriceEntity | null>({
    queryKey: ['pack-price', packId, currencyCode, countryCode],
    queryFn: async () => {
      if (!packId || !currencyCode) return null;
      const cached = queryClient.getQueryData<ResolvedPackPriceEntity | null>([
        'pack-price',
        packId,
        currencyCode,
        countryCode,
      ]);
      return cached ?? null;
    },
    enabled: !!packId && !!currencyCode,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}
